import { Button, Container } from 'components/atoms';
import RadialAudioPlayer from 'components/atoms/audio-player/RadialAudioPlayer';
import DataWrapper from 'components/molecules/data-wrapper/DataWrapper';
import ViewHint from 'components/molecules/hint/ViewHint';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { ChangeEvent, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiArrowsClockwise, PiDownload, PiFileAudio, PiFileX, PiTrash } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetTrackFileMetadata } from 'services/tracks/file';
import { ITrackMutationForm } from 'services/tracks/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { readableFileSize } from 'utils/components/inputs/file';
import { getFileExtension } from 'utils/global/file.utils';

const TrackFile = () => {
  const { values, status, setFieldValue }: IFormikContextType<ITrackMutationForm> =
    useFormikContext<ITrackMutationForm>();

  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const metadata = useGetTrackFileMetadata(
    values.id,
    !!values.mp3Link?.length && !!values.id.length,
  );

  const onDeleteTrack = async () => {
    if (!values.mp3Link && !values.mp3PreviewLink && !values.wavLink) {
      setFieldValue('file', undefined);
    }

    if (values.mp3Link && values.mp3PreviewLink && values.wavLink && !values.file) {
      setFieldValue('originalFileLink', undefined);
      setFieldValue('mp3Link', undefined);
      setFieldValue('mp3PreviewLink', undefined);
      setFieldValue('wavLink', undefined);
    }
    if (values.file) {
      setFieldValue('file', undefined);
    }
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files && event.currentTarget.files[0];

    if (file) {
      console.log(getFileExtension(file));

      const extension = getFileExtension(file);

      if (extension !== 'wav' && extension !== 'flac')
        return toast.error(t('formik-error.invalid-file-extension'));

      setFieldValue('file', file);
      event.currentTarget.value = '';
    }
  };

  const navigate = useNavigate();
  const { setForm } = useStoreState();

  const onGoToStep = (step: string) => {
    setForm('trackForm', values);
    navigate(`/track/${values.id ? `edit/${values.id}` : 'create'}/${step}`);
  };

  return (
    <StepsTab nextName="data" onNext={() => onGoToStep('data')} isFirst>
      <DataWrapper>
        <ViewHint hintId="track-file" />
        {!status?.displayMode && !values.file && !values.mp3PreviewLink && (
          <Button
            variant={BUTTON_VARIANT.white}
            size="large"
            fullWidth
            onClick={() => inputRef.current?.click()}
          >
            <Button.Icon icon={<PiFileAudio />} />
            <Button.Text text={t('button.add-audio-file')} />
          </Button>
        )}
        <input ref={inputRef} className="hidden" type="file" onChange={onInputChange} />
        <Container>
          <Container.Content
            className="flex flex-row justify-between items-center p-8 px-[16px]"
            style={{ backgroundColor: 'var(--background)' }}
          >
            {!values.file && !values.originalFileLink && !values.mp3PreviewLink && (
              <div className="flex flex-col items-center justify-center w-full h-full gap-5 p-[20px]">
                <PiFileX className="h-[50px] w-[50px] opacity-25 " />
              </div>
            )}
            {!values.file && !values.mp3PreviewLink && values.originalFileLink && (
              <div className="flex flex-col items-center justify-center w-full h-full gap-5 p-[40px]">
                <PiArrowsClockwise
                  className="h-[150px] w-[150px] opacity-25 animate-spin"
                  style={{ animationDuration: `3000ms` }}
                />
                <p className="opacity-50 text-lg">{t('molecules.sections.converting')}</p>
              </div>
            )}
            {values.mp3PreviewLink && !values.file && (
              <RadialAudioPlayer
                key={values.mp3PreviewLink}
                playerId={values.mp3PreviewLink ?? ''}
                audioSrc={values.mp3PreviewLink ?? ''}
              />
            )}

            {values.mp3PreviewLink && !values.file && metadata.data && (
              <div className="flex flex-col gap-2 w-full">
                <p className="flex text-base">{metadata.data.musicFileFileName}</p>
                <p className="opacity-65">{readableFileSize(metadata.data.musicFileSizeInMb)}</p>
              </div>
            )}

            {values.file && (
              <RadialAudioPlayer
                key={URL.createObjectURL(values.file)}
                playerId={URL.createObjectURL(values.file) ?? ''}
                audioSrc={URL.createObjectURL(values.file) ?? ''}
              />
            )}
            {values.file && (
              <div className="flex flex-col gap-2 w-full">
                <p className="flex text-base">{values.file?.name}</p>
                <p className="opacity-65">{readableFileSize(values.file?.size)}</p>
              </div>
            )}
            {/* {values.mp3PreviewLink && !values.file && (
              <div className="flex items-center flex-row gap-2">
                <MoleculeButton.AudioDownload
                  isClear
                  mp3Url={values.mp3Link}
                  flacUrl={values.flacLink}
                  wavUrl={values.wavLink}
                  disabled={!values.wavLink && !values.mp3Link}
                />
              </div>
            )} */}
          </Container.Content>
        </Container>
        {values.mp3PreviewLink && status?.displayMode && (
          <Button
            variant={BUTTON_VARIANT.white}
            fullWidth
            onClick={() => inputRef.current?.click()}
          >
            <Button.Icon icon={<PiDownload />} />
            <Button.Text text={t('button.download-audio-file')} />
          </Button>
        )}
        {!status?.displayMode && (values.file || values.mp3PreviewLink) && (
          <div className="flex w-full gap-4">
            <Button
              variant={BUTTON_VARIANT.white}
              fullWidth
              onClick={() => inputRef.current?.click()}
            >
              <Button.Icon icon={<PiFileAudio />} />
              <Button.Text text={t('button.change-audio-file')} />
            </Button>
            <Button
              variant={BUTTON_VARIANT.white}
              onlyIcon
              disabled={!values}
              onClick={onDeleteTrack}
            >
              <Button.Icon icon={<PiTrash />} />
            </Button>
          </div>
        )}{' '}
      </DataWrapper>
    </StepsTab>
  );
};

export default TrackFile;
